import React from 'react';
import { Box } from '@mui/material';
import { containerSizes, semanticColors, iconSizes } from 'design-system';

/**
 * CircularIcon Props
 */
export interface CircularIconProps {
  /** Icon element to wrap */
  icon: React.ReactNode;
  
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  
  /** Background color */
  backgroundColor?: string;
  
  /** Icon color */
  iconColor?: string;
  
  /** Additional CSS class */
  className?: string;
  
  /** Test ID */
  testId?: string;
}

/**
 * Size mappings using design system tokens
 * Icon sizes scale proportionally with container sizes
 */
const sizeMap = {
  sm: { container: containerSizes.circularIcon.sm, icon: iconSizes.sm },
  md: { container: containerSizes.circularIcon.md, icon: iconSizes.smMd },
  lg: { container: containerSizes.circularIcon.lg, icon: iconSizes.lg },
};

/**
 * CircularIcon Component
 * 
 * Wraps any icon in a circular container.
 * 
 * Design Specs:
 * - Circular background
 * - Centered icon
 * - Configurable size and colors
 * 
 * @example
 * ```tsx
 * <CircularIcon icon={<BedIcon />} size="md" />
 * ```
 */
export default function CircularIcon({
  icon,
  size = 'md',
  backgroundColor = semanticColors.lightGray,
  iconColor,
  className,
  testId,
}: CircularIconProps) {
  const { container, icon: iconSize } = sizeMap[size];

  return (
    <Box
      className={className}
      data-testid={testId}
      sx={{
        width: container,
        height: container,
        borderRadius: '50%',
        backgroundColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
          width: iconSize,
          height: iconSize,
          display: 'block',
          ...(iconColor && { fill: iconColor }),
        },
      }}
    >
      {icon}
    </Box>
  );
}
